import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { UserRoleDialogProps, UserRole } from '../types';

const UserRoleDialog: React.FC<UserRoleDialogProps> = ({
  user,
  open,
  onClose,
  onSave,
  availableRoles,
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Extract current user roles
  const getCurrentUserRoles = (): string[] => {
    if (!user?.roles) return [];
    
    return user.roles.map(role => {
      if (typeof role === 'object' && (role as UserRole).name) {
        return (role as UserRole).name;
      }
      if (typeof role === 'string') {
        return role;
      }
      return '';
    }).filter(Boolean);
  };

  // Initialize selected roles when dialog opens
  useEffect(() => {
    if (open && user) {
      setSelectedRoles(getCurrentUserRoles());
    }
  }, [open, user]);

  const handleRoleToggle = (roleValue: string) => {
    setSelectedRoles(prev => {
      if (prev.includes(roleValue)) {
        return prev.filter(role => role !== roleValue);
      } else {
        return [...prev, roleValue];
      }
    });
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      await onSave(user.id, selectedRoles);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  if (!user) return null;

  const currentRoles = getCurrentUserRoles();
  const hasChanges = JSON.stringify(selectedRoles.sort()) !== JSON.stringify(currentRoles.sort());

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Gestionar Roles</DialogTitle>
          <DialogDescription>
            Selecciona los roles para <strong>{user.name}</strong> ({user.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current roles display */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Roles actuales:</h4>
            <div className="flex flex-wrap gap-1">
              {currentRoles.length > 0 ? (
                currentRoles.map(role => {
                  const roleInfo = availableRoles.find(r => r.value === role);
                  return (
                    <Badge key={role} variant="secondary">
                      {roleInfo?.label || role}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-sm text-gray-500">Sin roles asignados</span>
              )}
            </div>
          </div>

          {/* Role selection */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Seleccionar roles:</h4>
            <div className="space-y-2">
              {availableRoles.map(role => (
                <div key={role.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`role-${role.value}`}
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={() => handleRoleToggle(role.value)}
                    disabled={saving}
                  />
                  <label
                    htmlFor={`role-${role.value}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Selected roles preview */}
          {selectedRoles.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Roles seleccionados:</h4>
              <div className="flex flex-wrap gap-1">
                {selectedRoles.map(role => {
                  const roleInfo = availableRoles.find(r => r.value === role);
                  return (
                    <Badge key={role} variant="default">
                      {roleInfo?.label || role}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            loading={saving}
          >
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserRoleDialog;